import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { CircleAlert, ShieldCheck } from 'lucide-react';

import { getRedirectionDataAction, redirectAction } from 'actions/workflow';
import { Button } from 'components/Button';
import Spinner from 'components/Spinner';
import Modal from 'components/Modal';

const Redirection = ({
  dispatch,
  navigate,
  searchParams,
}) => {
  const token = searchParams.get('token');
  const firstName = useSelector((state) => get(state, 'workflow.applicantFirstName'));
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    dispatch(getRedirectionDataAction({
      token,
      success: () => setIsFetchingData(false),
      fail: () => navigate('/'),
    }))
  }, []);

  const continueRedirection = () => {
    setIsLoading(true);
    dispatch(redirectAction({
      token,
      success: (res) => {
        navigate(get(res, 'data.url') || '/');
        setIsLoading(false);
      },
      fail: (err) => {
        setIsLoading(false);
        const errorMessage = get(err, 'data.failure.errors.0.message')
              || get(err, 'data.errorMessage')
              || get(err, 'data.errorMessages.0')
              || 'Something went wrong';
        setErrorMessage(errorMessage);
      }
    }));
  };

  const handleCloseModal = () => {
    setErrorMessage(null);
  };

  if (isFetchingData) {
    return (
      <div className="text-center mb-6">
        <Spinner size={12} color="blue-400" />
      </div>
    );
  };

  return (
    <>
      <div className="flex items-center justify-center gap-2 mb-4">
        <h2 className="page-title text-xl md:text-2xl text-center font-semibold text-slate-800 leading-tight">
          Welcome Back, {firstName}!
        </h2>
      </div>
      <div className="text-center mb-6 flex flex-col gap-6 items-center justify-center">
        <p className="text-gray-500 leading-relaxed">
          Pick up right where you left off.
          <br />
          We&apos;ll just confirm it&apos;s you first.
        </p>
        <Button
          className="max-w-full w-full"
          label="Continue"
          onClick={continueRedirection}
          isLoading={isLoading}
        />
        <div className="w-full flex items-start gap-2 bg-slate-50 rounded-lg px-3 py-2 text-left text-xs md:text-sm text-slate-500">
          <ShieldCheck className="h-4 w-4 md:h-5 md:w-5 shrink-0 text-blue-500 mt-0.5 md:mt-0" />
          <p>
            LendingUSA will never ask for your account passwords, seed phrase, or private keys.
          </p>
        </div>
        <p className="text-xs md:text-sm text-slate-500">
          Need help?
          {' '}
          <a href="tel:+18447216961" className="whitespace-nowrap text-blue-500 font-semibold">
            (844) 721-6961
          </a>
        </p>
      </div>
      {errorMessage && (
        <Modal title="An error occurred" onClose={handleCloseModal}>
          <div className="flex flex-col justify-center items-center gap-2">
            <CircleAlert className="h-12 w-12 text-red-500" />
            <p className="text-slate-600 mb-2">{errorMessage}</p>
            <Button className="bg-red-500 text-white w-full" label="OK" onClick={handleCloseModal} />
          </div>
        </Modal>
      )}
    </>
  );
};

Redirection.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default Redirection;
